import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    Stack,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { apiHandler } from '../../../utils/api/responseHandler';
import config from '../../../config';
import { useAuth } from '../../../hooks/auth/useAuth';
import logger from '../../../utils/logging/logger';
import { useCart } from '../../../utils/cart/CartContext';
import { quantityUpdateValidator, type QuantityUpdateResponse } from '../../../utils/api/validation';
import CartSummaryPrintModal from './CartSummaryPrintModal';
import PricingDisplay from '../../shared/PricingDisplay';
import { usePricing } from '../../../hooks/quoter/usePricing';
import type { QuoteData } from '../../../types/quoter/types';
import CartItem from './CartItem';
import type { CartItem as CartItemType } from './CartItem';
import { trackClarityEvent, setClarityTag } from '../../../utils/analytics/clarityEvents';

const componentName = 'CartDrawer';

// Updated CartItemDetails interface to match the new API response structure
// Making it compatible with QuoteData by ensuring all required fields are present
// CartItem interface now imported from CartItem.tsx component

interface CartDrawerProps {
    open: boolean;
    onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [cartItems, setCartItems] = useState<CartItemType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [shippingCost, setShippingCost] = useState<number>(0);
    const [confirmDeleteItem, setConfirmDeleteItem] = useState<CartItemType | null>(null);
    const [printModalOpen, setPrintModalOpen] = useState<boolean>(false);
    const { user, isAuthenticated, isAnonymous } = useAuth();
    const { refreshCart, cartVersion } = useCart();

    type OrderSummary = {
        OrderId: number;
        SubTotal_CustomerPrice: number;
        SubTotal_CustomerDiscount: number;
        Total_CustomerQuotedPrice: number;
        SubTotal_ResalePrice: number;
        SubTotal_ResaleDiscount: number;
        Total_ResalePrice: number;
        ShippingEstimate: number;
        Taxes: number;
        CartTotal_Customer: number;
        CartTotal_Reseller: number;
        UpdateTime: string;
        CreateTime: string;
    };

    const fetchOrderSummary = async (orderId: number) => {
        try {
            const resp = await apiHandler.fetch<{ success?: boolean; summary?: OrderSummary }>(
                `${config.API_URL}/api/order/${orderId}/summary`,
                { validateFn: (d) => d as any }
            );
            if (!resp.error && resp.data?.summary) {
                setOrderSummary(resp.data.summary);
                setShippingCost(Number(resp.data.summary.ShippingEstimate ?? 0));
            } else {
                setOrderSummary(null);
                setShippingCost(0);
            }
        } catch (e) {
            // Log as warning since 404 is expected when order is empty/deleted
            logger.log('LogWarning', componentName, 'Order summary not available', e);
            setOrderSummary(null);
            setShippingCost(0);
        }
    };

    const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);

    // Discount code UI state
    const [discountCodes, setDiscountCodes] = useState<Array<{ DiscountCodeId: number; DiscountHeader: string }>>([]);
    const [selectedDiscountCodeId, setSelectedDiscountCodeId] = useState<number | ''>('');
    const [discountCodeText, setDiscountCodeText] = useState<string>('');
    const [applyingDiscount, setApplyingDiscount] = useState<boolean>(false);
    const [discountNotice, setDiscountNotice] = useState<string>('');
    const [showDiscountSection, setShowDiscountSection] = useState<boolean>(false);

    // CONSOLIDATED FETCH FUNCTIONS - Regular functions to prevent dependency cascade
    // Accept forceUserId to avoid stale closure issues when userId changes
    const fetchCartItems = async (forceUserId?: number) => {
        // PHASE 1: Allow anonymous users to see their cart
        // Only require a valid userId, not full authentication
        const currentUserId = forceUserId ?? user?.userId;
        if (!currentUserId || currentUserId <= 0) {
            setCartItems([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiHandler.fetch(
                `${config.API_URL}/api/order-group/${currentUserId}`,
                {
                    validateFn: (data) => data,
                }
            );

            if (response.error) throw response.error;

            const itemsWithDetails: CartItemType[] = [];
            for (const item of (response.data as any[] || [])) {
                try {
                    const [detailsResponse, optionsResponse] = await Promise.all([
                        apiHandler.fetch(
                            `${config.API_URL}/api/order/${item.Order_QuoteId}`,
                            { validateFn: (data) => data }
                        ),
                        apiHandler.fetch(
                            `${config.API_URL}/api/order-options/${item.Order_QuoteId}`,
                            { validateFn: (data) => data }
                        )
                    ]);

                    if (!detailsResponse.error && detailsResponse.data) {
                        itemsWithDetails.push({
                            ...item,
                            details: detailsResponse.data,
                            options: optionsResponse.data || [],
                            quantity: item.Quantity || 1
                        });
                    }
                } catch (detailErr) {
                    logger.log('LogError', componentName, 'Error fetching item details', detailErr);
                }
            }

            setCartItems(itemsWithDetails);
            logger.log('LogVerbose', componentName, 'Cart items fetched', { count: itemsWithDetails.length });

            // Load DB totals/summary (includes shipping) if we have items
            if (itemsWithDetails.length > 0) {
                await fetchOrderSummary(itemsWithDetails[0].OrderId);
            }
        } catch (err) {
            logger.log('LogError', componentName, 'Error fetching cart items', err);
            setError('Failed to load cart items. Please try again.');
            setCartItems([]);
        } finally {
            setLoading(false);
        }
    };

    // Accept forceUserId to avoid stale closure issues when userId changes
    const fetchDiscountCodes = async (forceUserId?: number) => {
        try {
            // Move auth checks inside function to prevent dependency issues
            const currentUserId = forceUserId ?? user?.userId;
            if (!currentUserId || currentUserId <= 0) return;
            if (!(user?.isAdmin || user?.isReseller)) {
                setDiscountCodes([]);
                return;
            }

            type DiscountCode = { DiscountCodeId: number; DiscountHeader: string };

            const resp = await apiHandler.fetch<{ success?: boolean; codes?: DiscountCode[] }>(
                `${config.API_URL}/api/discount-codes/${currentUserId}?discountCodeId=0`,
                { validateFn: (data) => data as any }
            );

            setDiscountCodes(Array.isArray(resp.data?.codes) ? resp.data!.codes! : []);

        } catch (e) {
            logger.log('LogError', componentName, 'Error fetching discount codes', e);
            setDiscountCodes([]);
        }
    };

    // useEffect depends on 'open', 'user?.userId', and 'cartVersion' to ensure cart loads
    // when user is ready AND when cart contents change (e.g., item added from AddToCartButton).
    // Pass userId explicitly to avoid stale closure issues during user transitions.
    useEffect(() => {
        if (open && user?.userId) {
            trackClarityEvent('open_cart_drawer');
            setClarityTag('cart_action', 'open_drawer');

            const loadCartData = async () => {
                await Promise.all([
                    fetchCartItems(user?.userId),
                    fetchDiscountCodes(user?.userId)
                ]);
            };
            loadCartData().catch((err) => {
                logger.log('LogError', componentName, 'Error loading cart data', err);
            });
        }
    }, [open, user?.userId, cartVersion]); // cartVersion triggers refetch when cart is mutated

    const handleApplyDiscount = async () => {
        try {
            if (cartItems.length === 0) return;
            const orderId = cartItems[0].OrderId;
            const usingDropdown = discountCodes.length > 0;

            const hasSelectedId =
                selectedDiscountCodeId !== '' &&
                selectedDiscountCodeId !== null &&
                !Number.isNaN(Number(selectedDiscountCodeId)) &&
                Number(selectedDiscountCodeId) >= 0;

            if (usingDropdown && !hasSelectedId) {
                setDiscountNotice('Please select a discount code.');
                setTimeout(() => setDiscountNotice(''), 3000);
                return;
            }
            if (!usingDropdown && !discountCodeText.trim()) {
                setDiscountNotice('Please enter a discount code.');
                setTimeout(() => setDiscountNotice(''), 3000);
                return;
            }

            setApplyingDiscount(true);
            setDiscountNotice('');

            const body = usingDropdown
                ? { discountCodeId: Number(selectedDiscountCodeId) }
                : { discountCode: discountCodeText.trim() };

            type ApplyDiscountResponse = { success?: boolean; affected?: number; recordset?: unknown[] };

            const resp = await apiHandler.fetch<ApplyDiscountResponse>(
                `${config.API_URL}/api/order/${orderId}/apply-discount`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                    validateFn: (d) => d as ApplyDiscountResponse
                }
            );

            const affected = Number(resp.data?.affected ?? 0);

            if (affected === 0) {
                setDiscountNotice('That discount code was not valid.');
                setTimeout(() => setDiscountNotice(''), 5000);
            } else {
                setDiscountNotice('Discount applied. Prices updated.');
                setTimeout(() => setDiscountNotice(''), 3000);
                // Refresh cart to reflect recalculated pricing
                await fetchCartItems();
            }
        } catch (err) {
            logger.log('LogError', componentName, 'Error applying discount', err);
            setDiscountNotice('Failed to apply discount. Please try again.');
            setTimeout(() => setDiscountNotice(''), 5000);
        } finally {
            setApplyingDiscount(false);
        }
    };

    const handleRemoveItem = async () => {
        if (!confirmDeleteItem) return;

        try {
            const orderId = confirmDeleteItem.OrderId;

            await apiHandler.fetch(
                `${config.API_URL}/api/order-quote/${confirmDeleteItem.Order_QuoteId}`,
                {
                    method: 'DELETE'
                }
            );

            // Remove item from local state
            const updatedItems = cartItems.filter(item =>
                item.Order_QuoteId !== confirmDeleteItem.Order_QuoteId
            );
            setCartItems(updatedItems);

            // Refresh order summary if there are still items left
            if (updatedItems.length > 0) {
                try {
                    await fetchOrderSummary(orderId);
                } catch (summaryErr) {
                    // If order summary fails (e.g., 404 because order is empty/deleted),
                    // just clear the summary - don't show error to user
                    logger.log('LogWarning', componentName, 'Order summary not available after item removal', summaryErr);
                    setOrderSummary(null);
                    setShippingCost(0);
                }
            } else {
                // Clear summary if cart is now empty
                setOrderSummary(null);
                setShippingCost(0);
            }

            setConfirmDeleteItem(null);
            refreshCart();

        } catch (err) {
            logger.log('LogError', componentName, 'Error removing item from cart', err);
            setError('Failed to remove item from cart. Please try again.');
        }
    };

    const handleQuantityChange = async (index: number, newQuantity: number) => {
        // Ensure quantity is at least 1
        const quantity = Math.max(1, newQuantity);
        const item = cartItems[index];

        if (!item) return;

        try {
            setError(null);

            // Call API to update quantity on server
            const response = await apiHandler.fetch<QuantityUpdateResponse>(
                `${config.API_URL}/api/order-quote/${item.Order_QuoteId}/quantity`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ quantity }),
                    validateFn: (data) => quantityUpdateValidator.validate(data)
                }
            );

            if (response.error) throw response.error;

            // Update local state with confirmed quantity from server
            setCartItems(items => {
                const newItems = [...items];
                if (newItems[index]) {
                    newItems[index] = {
                        ...newItems[index],
                        quantity: response.data?.quantity || quantity
                    };
                }
                return newItems;
            });

            // Refresh order summary to get updated totals from database
            await fetchOrderSummary(item.OrderId);

            // Update cart count badge (now shows total quantity)
            refreshCart();

            logger.log('LogVerbose', componentName, 'Cart quantity updated successfully', {
                orderQuoteId: item.Order_QuoteId,
                newQuantity: quantity
            });

        } catch (err) {
            logger.log('LogError', componentName, 'Error updating cart quantity', err);
            setError('Failed to update quantity. Please try again.');
        }
    };

    const calculateCustomerSubtotal = () => {
        if (!orderSummary) {
            // Fallback: calculate from cart items if orderSummary not available.
            // Use QuotedPrice (post-discount) for customers, consistent with usePricing hook.
            // When no discount is applied, QuotedPrice == CalculatedPrice.
            return cartItems.reduce((sum, item) => {
                const pricing = item.details;
                const unitPrice = Number(pricing?.QuotedPrice || pricing?.CalculatedPrice || 0);
                return sum + (unitPrice * item.quantity);
            }, 0);
        }
        return Number(orderSummary.Total_CustomerQuotedPrice || 0);
    };

    const calculateResellerSubtotal = () => {
        if (!orderSummary) {
            // Fallback: calculate from cart items if orderSummary not available
            return cartItems.reduce((sum, item) => {
                const pricing = item.details;
                const unitPrice = Number(pricing?.ResaleUserPrice || pricing?.QuotedPrice || 0);
                return sum + (unitPrice * item.quantity);
            }, 0);
        }
        return Number(orderSummary.Total_ResalePrice || 0);
    };

    const calculateTotal = () => {
        if (!orderSummary) {
            // Fallback: calculate from cart items if orderSummary not available
            // Admins see customer pricing, only resellers see reseller pricing
            const isReseller = !!user?.isReseller;
            const subtotal = isReseller ? calculateResellerSubtotal() : calculateCustomerSubtotal();
            return subtotal + shippingCost;
        }
        // Admins see customer pricing, only resellers see reseller pricing
        const isReseller = !!user?.isReseller;
        return isReseller
            ? Number(orderSummary.CartTotal_Reseller || 0)
            : Number(orderSummary.CartTotal_Customer || 0);
    };

    // Format sail options for display
    const formatSailOptions = (options: any[]) => {
        if (!options || options.length === 0) {
            return ["No options selected"];
        }

        const selectedOptions = options
            .filter(option => option.ValueSelected > 0)
            .map(option => {
                const cost = option.OptionQuotedPrice !== 0 ? ` (+$${Math.round(option.OptionQuotedPrice)})` : '';

                // Check if OptionDetails already contains the quantity
                const alreadyHasQuantity = option.OptionDetails.includes(': ') ||
                    option.OptionDetails.includes(option.ValueSelected.toString());

                if (option.ValueSelected === 1 || alreadyHasQuantity) {
                    return `${option.OptionDetails}${cost}`;
                } else {
                    return `${option.OptionDetails}: ${option.ValueSelected}${cost}`;
                }
            });

        return selectedOptions.length > 0 ? selectedOptions : ["No options selected"];
    };

    // Get cloth name helper
    const getClothName = (item: CartItemType) => {
        if (item.details?.ClothDisplayName) {
            return item.details.ClothDisplayName;
        }
        return "Standard";
    };

    // Helper to determine if sail is a headsail
    const isHeadsail = (item: CartItemType) => {
        const sailName = item.details?.SailName?.toLowerCase() || '';
        const percentLP = item.details?.PercentLP || 0;

        // Check if sail name contains 'headsail' or if it has a valid LP percentage
        return sailName.includes('headsail') || percentLP > 0;
    };

    // Helper to format LP percentage
    const formatLPPercent = (item: CartItemType) => {
        const percentLP = item.details?.PercentLP;
        if (percentLP && percentLP > 0) {
            return `${percentLP}% LP`;
        }
        return null;
    };

    const handlePrintCart = () => {
        // Temporarily close the cart drawer to avoid conflicts during printing
        onClose();
        // Small delay to ensure drawer animation completes before opening print modal
        setTimeout(() => {
            setPrintModalOpen(true);
        }, 300);
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 500 } } }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" component="h2">
                            <ShoppingCartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Your Cart
                        </Typography>
                        <IconButton onClick={onClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    ) : cartItems.length === 0 ? (
                        <Typography align="center" sx={{ p: 3 }}>
                            Your cart is empty.
                        </Typography>
                    ) : (
                        <>
                            {cartItems.map((item, index) => (
                                <CartItem
                                    key={`${item.Order_QuoteId}-${index}`}
                                    item={item}
                                    index={index}
                                    isResellerUser={!!user?.isReseller}
                                    onUpdateQuantity={handleQuantityChange}
                                    onRemove={setConfirmDeleteItem}
                                />
                            ))}

                            {/* Summary Section */}
                            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                                <Stack spacing={2}>
                                    {/* Show retail price and discount when a discount is applied */}
                                    {orderSummary && Number(orderSummary.SubTotal_CustomerDiscount || 0) > 0 && (
                                        <>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography color="text.secondary">Retail Price:</Typography>
                                                <Typography color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                    ${Math.round(Number(orderSummary.SubTotal_CustomerPrice || 0)).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography color="success.main">Discount Applied:</Typography>
                                                <Typography color="success.main">
                                                    -${Math.round(Number(orderSummary.SubTotal_CustomerDiscount || 0)).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                    <Divider />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="h6">Total:</Typography>
                                        <Typography variant="h6">${Math.round(calculateTotal()).toLocaleString()}</Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                                        Plus shipping and applicable taxes that will be calculated during order review
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                        *All prices displayed are estimates and subject to review.
                                    </Typography>

                                    {/* Discount Code Section - Collapsible and less intrusive */}
                                    {!showDiscountSection ? (
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => setShowDiscountSection(true)}
                                            sx={{
                                                textTransform: 'none',
                                                color: 'text.secondary',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    textDecoration: 'underline'
                                                }
                                            }}
                                        >
                                            Have a discount code?
                                        </Button>
                                    ) : (
                                        <Box>
                                            {discountCodes.length > 0 ? (
                                                // DROPDOWN MODE
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                                    <FormControl fullWidth>
                                                        <InputLabel id="discount-select-label">Discount Code</InputLabel>
                                                        <Select
                                                            labelId="discount-select-label"
                                                            label="Discount Code"
                                                            value={selectedDiscountCodeId}
                                                            onChange={(e) => setSelectedDiscountCodeId(Number(e.target.value))}
                                                        >
                                                            {discountCodes.map(dc => (
                                                                <MenuItem key={dc.DiscountCodeId} value={dc.DiscountCodeId}>
                                                                    {dc.DiscountHeader}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <Button variant="contained" onClick={handleApplyDiscount} disabled={applyingDiscount}>
                                                        {applyingDiscount ? 'Applying...' : 'Apply'}
                                                    </Button>
                                                </Stack>
                                            ) : (
                                                // TEXTBOX MODE
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                                    <TextField
                                                        fullWidth
                                                        label="Discount Code"
                                                        size="small"
                                                        value={discountCodeText}
                                                        onChange={(e) => setDiscountCodeText(e.target.value)}
                                                        autoComplete="off"
                                                    />
                                                    <Button variant="contained" onClick={handleApplyDiscount} disabled={applyingDiscount} size="small">
                                                        {applyingDiscount ? 'Applying...' : 'Apply'}
                                                    </Button>
                                                </Stack>
                                            )}

                                            {/* Reserved space for discount message - prevents layout shift */}
                                            <Box sx={{ minHeight: '40px', mt: 1, mb: 1 }}>
                                                {!!discountNotice && (
                                                    <Alert
                                                        severity={discountNotice.includes('not valid') ? 'warning' : 'success'}
                                                        variant="outlined"
                                                        sx={{
                                                            transition: 'opacity 0.2s ease-in-out',
                                                            opacity: discountNotice ? 1 : 0,
                                                            py: 0.5,
                                                            fontSize: '0.875rem',
                                                            '& .MuiAlert-icon': {
                                                                fontSize: '1rem'
                                                            }
                                                        }}
                                                    >
                                                        {discountNotice}
                                                    </Alert>
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        size="large"
                                        disabled={cartItems.length === 0}
                                        onClick={() => {
                                            trackClarityEvent('start_checkout');
                                            onClose();
                                            navigate('/checkout');
                                        }}
                                        sx={{
                                            '&.Mui-disabled': {
                                                backgroundColor: 'rgba(255, 140, 62, 0.6)', // MARITIME.SUNSET_ORANGE with opacity
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            }
                                        }}
                                    >
                                        Submit Quote for Review...
                                    </Button>

                                    {/* Print Cart - Demoted to text link */}
                                    <Typography
                                        component="button"
                                        onClick={handlePrintCart}
                                        sx={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'text.secondary',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            mt: 2,
                                            mx: 'auto',
                                            display: 'block',
                                            '&:hover': {
                                                color: 'primary.main',
                                            }
                                        }}
                                    >
                                        Print cart pricing
                                    </Typography>
                                </Stack>
                            </Box>
                        </>
                    )}
                </Box>
            </Drawer>

            {/* Cart Summary Print Modal */}
            <CartSummaryPrintModal
                open={printModalOpen}
                onClose={() => {
                    setPrintModalOpen(false);
                    // Reopen the cart drawer after closing print modal
                    setTimeout(() => {
                        if (cartItems.length > 0) {
                            // Only reopen if there are items in cart
                            // Note: This requires the parent to handle reopening
                            // For now, user will need to manually reopen cart
                        }
                    }, 100);
                }}
                cartItems={cartItems}
                shippingCost={shippingCost}
                subtotal={user?.isReseller ? calculateResellerSubtotal() : calculateCustomerSubtotal()}
                total={calculateTotal()}
                showResellerPricing={!!user?.isReseller}
            />

            {/* Confirm Delete Dialog */}
            <Dialog
                open={!!confirmDeleteItem}
                onClose={() => setConfirmDeleteItem(null)}
            >
                <DialogTitle>Remove Item</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove this item from your cart?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteItem(null)}>Cancel</Button>
                    <Button onClick={handleRemoveItem} color="error" autoFocus>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CartDrawer;
